import { useCallback, useContext, useEffect, useRef, useState } from "react";
import { useDebouncedValue } from "@mantine/hooks";
import { useSearchParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import { TextInput } from "react-hook-form-mantine";
import { Grid, Paper, Stack, Text } from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { extractDirtyData } from "../../utils/dirtyData";
import { openScheduleModal } from "./ScheduleModal";
import { useConfirm } from "../../hooks/useConfirm";
import { useGetTags } from "../../api/tag";
import {
  useGetCampaignRecipientCount,
  fetchCampaignTemplateBody,
} from "../../api/campaigns";
import EmailPreview from "./EmailPreview";
import PlaceholderMenu from "./PlaceholderMenu";
import CampaignEditor from "./CampaignEditor";
import { CAMPAIGN_TEMPLATES } from "./campaignTemplates";
import TargetAudience from "./TargetAudience";
import CampaignAttachments from "./CampaignAttachments";
import CampaignActions from "./CampaignActions";
import { getCurrentWorkspace } from "../../services/userConfig";
import { AuthContext } from "../../services/context";
import { web_createEmailCampaignSchema } from "@congrs/schemas";
import { isContentEmpty } from "@congrs/utils";
import resolver from "@congrs/resolver";

const CampaignForm = ({
  initialData,
  onSubmit,
  isSubmitting,
  mode = "create",
  event,
  onSendNow,
  onSchedule,
}) => {
  const [audienceType, setAudienceType] = useState(
    initialData?.targetedTagIds?.length > 0 ? "tags" : "all"
  );

  const confirm = useConfirm();
  const { user } = useContext(AuthContext);
  const { eventId } = getCurrentWorkspace(user);
  const currentEventId = initialData?.eventId ?? eventId;

  const { data: tags } = useGetTags({
    eventId: currentEventId,
    type: "people",
    subtype: "categoryOfUser",
  });

  const defaultValues = {
    subject: initialData?.subject || "",
    content: initialData?.content || null,
    contentHtml: initialData?.contentHtml || "",
    targetedTagIds: initialData?.targetedTagIds
      ? initialData.targetedTagIds.map(String)
      : null,
    attachments: initialData?.attachments ?? [],
  };

  const {
    control,
    handleSubmit,
    formState: { isDirty, dirtyFields },
    watch,
    setValue,
  } = useForm({
    defaultValues,
    resolver: resolver(web_createEmailCampaignSchema),
  });

  const [previewContent, setPreviewContent] = useState(
    initialData?.content || null
  );
  const [contentError, setContentError] = useState(null);
  const [debouncedPreviewContent] = useDebouncedValue(previewContent, 300);
  const targetedTagIds = watch("targetedTagIds");
  const attachments = watch("attachments") || [];

  // Content pushed into the editor when a template is applied. The version
  // counter is the signal the editor watches (see CampaignEditor).
  const [appliedTemplate, setAppliedTemplate] = useState({
    content: null,
    version: 0,
  });

  const contentHtml = watch("contentHtml");
  // Tiptap serializes an empty doc as "<p></p>"; isContentEmpty strips tags so
  // that counts as empty. Used to disable Send/Schedule, which require a body.
  const isBodyEmpty = isContentEmpty(contentHtml);

  const handleSelectTemplate = async (template) => {
    // Only pre-fill the subject if the admin hasn't typed one.
    if (template.subject && !watch("subject")) {
      setValue("subject", template.subject, { shouldDirty: true });
    }

    setValue("attachments", template.attachments ?? [], { shouldDirty: true });

    let bodyHtml = "";
    if (template.bodyTemplate) {
      try {
        bodyHtml = await fetchCampaignTemplateBody({
          templateName: template.bodyTemplate,
          eventId: currentEventId,
        });
      } catch (error) {
        notifications.show({
          color: "red",
          title: "Could not load template",
          message: error.message || "Please try again.",
        });
        return;
      }
    }

    // Push the body into the editor; its onChange syncs content/contentHtml.
    setAppliedTemplate((prev) => ({
      content: bodyHtml,
      version: prev.version + 1,
    }));
  };

  const [searchParams] = useSearchParams();
  const templateId = searchParams.get("template");
  const appliedTemplateId = useRef(null);

  useEffect(() => {
    if (mode !== "create" || !templateId) return;
    if (appliedTemplateId.current === templateId) return;

    const template = CAMPAIGN_TEMPLATES.find((t) => t.id === templateId);
    if (!template) return;

    appliedTemplateId.current = templateId;
    handleSelectTemplate(template);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [templateId, mode]);

  const handleAttachmentsChange = (next) => {
    setValue("attachments", next, { shouldDirty: true });
  };

  const { data: recipientCountData } = useGetCampaignRecipientCount({
    eventId: currentEventId,
    tagIds:
      audienceType === "tags" && targetedTagIds?.length > 0
        ? targetedTagIds
        : null,
  });

  const handleContentChange = useCallback(
    ({ html, json }) => {
      setValue("content", json, { shouldDirty: true });
      // The button HTML already carries its inline style (built by
      // buildEmailButton and preserved through serialization by withButtonAttrs),
      // so the saved HTML keeps it without any post-processing.
      setValue("contentHtml", html, { shouldDirty: true });
      setPreviewContent(json);
      if (!isContentEmpty(html)) setContentError(null);
    },
    [setValue]
  );

  const hasValidContent = (data) => {
    if (isContentEmpty(data.contentHtml)) {
      setContentError("Email content cannot be empty");
      return false;
    }
    setContentError(null);
    return true;
  };

  const handleAudienceTypeChange = (value) => {
    setAudienceType(value);

    if (value === "all") {
      setValue("targetedTagIds", null, { shouldDirty: true });
    } else {
      setValue("targetedTagIds", watch("targetedTagIds") || [], {
        shouldDirty: true,
      });
    }
  };

  const handleFormSubmit = (data) => {
    const submitData =
      mode === "edit" ? extractDirtyData(data, dirtyFields) : data;

    onSubmit(submitData);
  };

  const canSendOrSchedule =
    mode === "create" || initialData?.status === "draft";

  const handleSendNowClick = () => {
    handleSubmit((data) => {
      if (!hasValidContent(data)) return;
      const submitData =
        mode === "edit" ? extractDirtyData(data, dirtyFields) : data;
      confirm({
        title: "Send Campaign Now?",
        message:
          "Are you sure you want to send this campaign immediately? This action cannot be undone.",
        onConfirm: () => onSendNow(submitData),
      });
    })();
  };

  const handleScheduleClick = () => {
    handleSubmit((data) => {
      if (!hasValidContent(data)) return;
      const submitData =
        mode === "edit" ? extractDirtyData(data, dirtyFields) : data;
      openScheduleModal((scheduledAt) => {
        onSchedule(submitData, scheduledAt);
      });
    })();
  };

  const totalRecipients =
    audienceType === "all"
      ? (event?.ticketsCount ?? 0)
      : (recipientCountData?.count ?? 0);

  return (
    <>
      <form onSubmit={handleSubmit(handleFormSubmit)}>
        <Grid gap="lg">
          <Grid.Col span={{ base: 12, md: 7 }}>
            <Stack gap="md">
              <Paper withBorder shadow="md" p="md" radius="md">
                <Stack gap="sm">
                  <TextInput
                    label="Email Subject"
                    placeholder="e.g. Welcome, {{firstName}}!"
                    control={control}
                    name="subject"
                    rightSection={
                      <PlaceholderMenu
                        onInsert={(value) => {
                          const current = watch("subject") || "";
                          setValue("subject", current + value, {
                            shouldDirty: true,
                          });
                        }}
                      />
                    }
                  />
                </Stack>
              </Paper>

              <TargetAudience
                control={control}
                audienceType={audienceType}
                onAudienceTypeChange={handleAudienceTypeChange}
                tags={tags}
                totalRecipients={totalRecipients}
              />

              <Paper withBorder shadow="md" p="md" radius="md">
                <Text fw={500} mb="xs">
                  Email Content
                </Text>
                <CampaignEditor
                  initialContent={defaultValues.content}
                  onChange={handleContentChange}
                  accentColor={event?.config?.accentColor}
                  appliedContent={appliedTemplate.content}
                  appliedVersion={appliedTemplate.version}
                />
                {contentError && (
                  <Text c="red" size="sm" mt="xs">
                    {contentError}
                  </Text>
                )}
              </Paper>

              <CampaignAttachments
                attachments={attachments}
                onChange={handleAttachmentsChange}
                eventId={currentEventId}
              />

              <CampaignActions
                mode={mode}
                isDirty={isDirty}
                isSubmitting={isSubmitting}
                isBodyEmpty={isBodyEmpty}
                canSendOrSchedule={canSendOrSchedule}
                onSendNow={onSendNow}
                onSchedule={onSchedule}
                onSendNowClick={handleSendNowClick}
                onScheduleClick={handleScheduleClick}
              />
            </Stack>
          </Grid.Col>

          <Grid.Col span={{ base: 12, md: 5 }}>
            <EmailPreview
              content={debouncedPreviewContent}
              eventName={event?.name}
            />
          </Grid.Col>
        </Grid>
      </form>
    </>
  );
};

export default CampaignForm;
